import { combineReducers } from 'redux'


import loanApplication, * as fromLoanApplication from './loanApplication'
import loanApplicationUser, * as fromLoanApplicationUser from './loanApplicationUser'
import creditSenseReport, * as fromCreditSenseReport from './creditSenseReport'
import income, * as fromIncome from './income'
import expense, * as fromExpense from './expense'
import errors, * as fromErrors from './errors'
import redirectUrl, * as fromRedirectUrl from './redirectUrl'
import commitment, * as fromCommitment from './commitment'
import creditHistory, * as fromCreditHistory from './creditHistory'
import asset, * as fromAsset from './asset'
import loanDetail, * as fromLoanDetail from './loanDetail'
import profile, * as fromProfile from './profile'
import residential_history, * as fromResidentialHistory from './residential_history'
import employment, * as fromEmployment from './employment'
import declaration, * as fromDeclaration from './declaration'
import options, * as fromOptions from './options'
import review, * as fromReview from './review'
import settings, * as fromSettings from './settings'
import verifyIdentity, * as fromVerifyIdentity from './verifyIdentity'

const reducer = combineReducers({
  loanApplication,
  loanApplicationUser,
  creditSenseReport,
  income,
  expense,
  commitment,
  creditHistory,
  asset,
  loanDetail,
  profile,
  verifyIdentity,
  residential_history,
  employment,
  declaration,
  review,
  redirectUrl,
  errors,
  options,
  settings,
})

const primaryUser = (loanApplicationUser) => {
  if (loanApplicationUser && loanApplicationUser.primary) {
    return loanApplicationUser.primary;
  } else {
    return loanApplicationUser;
  }
}

const secondaryUser = (loanApplicationUser) => {
  if (loanApplicationUser && loanApplicationUser.secondary) {
    return loanApplicationUser.secondary;
  } else {
    return loanApplicationUser;
  }
}

const currentLoanAPplicant = (state, applicant) => {
  return state.loanApplicationUser[applicant]
}

// LOAN APPLICATION USER REDUCER
export const getCurrentLoanApplicant = (state, applicant) => currentLoanAPplicant(state, applicant)
export const getLoanApplicationUser = (state) => primaryUser(state.loanApplicationUser)
export const getSecondaryLoanApplicationUser = (state) => secondaryUser(state.loanApplicationUser)
export const getSecondLoanApplicationUser = (state) => state.loanApplicationUser.secondary
export const getProgressBarValue = (state) => fromLoanApplicationUser.getProgressBarValue(primaryUser(state.loanApplicationUser))

export const getFlowState = (state) => fromLoanApplicationUser.getFlowState(primaryUser(state.loanApplicationUser))
export const getFirstName = (state) => fromLoanApplicationUser.getFirstName(state.loanApplicationUser)
export const getMiddleName = (state) => fromLoanApplicationUser.getMiddleName(state.loanApplicationUser)
export const getSurname = (state) => fromLoanApplicationUser.getSurname(state.loanApplicationUser)
export const getEmail = (state) => fromLoanApplicationUser.getEmail(state.loanApplicationUser)
export const getMobile = (state) => fromLoanApplicationUser.getMobile(state.loanApplicationUser)
export const getSidebar = (state) => fromLoanApplicationUser.getSidebar(primaryUser(state.loanApplicationUser))
export const isSecondaryUser = (state) => getLoanApplicationUser(state).role === 'secondary'

// LOAN APPLICATION REDUCER
export const getLoanApplication = (state) => state.loanApplication
export const getLoanApplicationPurpose = (state) => fromLoanApplication.getLoanApplicationPurpose(state.loanApplication)
export const getLoanApplicationTarget = (state) => fromLoanApplication.getLoanApplicationTarget(state.loanApplication)
export const getLoanApplicationState = (state) => fromLoanApplication.getLoanApplicationState(state.loanApplication)
export const getLoanApplicationId = (state) => fromLoanApplication.getLoanApplicationId(state.loanApplication)
export const getRefinanceReason = (state) => fromLoanApplication.getRefinanceReason(state.loanApplication)
export const loanApplicationConditional = (state) => fromLoanApplication.loanApplicationConditional(state.loanApplication)

export const getExpensesDisabled = (state) => isSecondaryUser(state) && getLoanApplication(state).primaryEntersData

// CREDIT SENSE REPORT REDUCER
export const getCreditSenseStatus = (state) => fromCreditSenseReport.getCreditSenseStatus(state.creditSenseReport)
export const getCreditSenseReference = (state) => fromCreditSenseReport.getCreditSenseReference(state.creditSenseReport)

// INCOME REDUCER
export const getAdditionalIncomes = (state, applicant) => fromIncome.getAdditionalIncomes(state.income)
export const getMainIncome = (state, applicant) => fromIncome.getMainIncome(state.income)
export const getIncome = (state, props) => fromIncome.getIncome(state.income, props)

// REDIRECT URL REDUCER
export const getRedirectUrlReference = (state) => fromRedirectUrl.getRedirectUrlReference(state.redirectUrl)

// ERRORS REDUCER
export const getErrorMessage = (state) => fromErrors.getErrorMessage(state.errors)

// COMMITMENT REDUCER
export const getCommitments = (state) => fromCommitment.getCommitments(state.commitment)
export const getCommitment = (state, id) => fromCommitment.getCommitment(state.commitment, id)
export const getCommitmentIds = (state) => fromCommitment.getCommitmentIds(state.commitment)
export const getActiveCommitmentIds = (state) => fromCommitment.getActiveCommitmentIds(state.commitment)

// CREDIT HISTORY REDUCER
export const getCreditHistory = (state) => {
  return fromCreditHistory.getCreditHistory(state.creditHistory)
}

// ASSETS REDUCER
export const getAsset = (state, id) => fromAsset.getAsset(state.asset, id)
export const getAssets = (state) => fromAsset.getAssets(state.asset)
export const getCashAssetIds = (state) => fromAsset.getCashAssetIds(state.asset)
export const getDepositAssetIds = (state) => fromAsset.getDepositAssetIds(state.asset)

// RESIDENTIAL HISTORIES REDUCER
export const getResidentialHistories = (state) => fromResidentialHistory.getResidentialHistories(state.residential_history)
export const getResidentialHistoryIds = (state) => fromResidentialHistory.getResidentialHistoryIds(state.residential_history)
export const getResidentialHistory = (state, id) => fromResidentialHistory.getResidentialHistory(state.residential_history, id)

// LOAN DETAIL REDUCER
export const getLoanDetail = (state) => state.loanDetail
export const getLoanDetailParams = (state) => fromLoanDetail.getLoanDetailParams(state.loanDetail)
export const getLoanDetailInterestType = (state, rateType) => fromLoanDetail.getInterest(state.loanDetail, rateType)
export const getLoanDetailSettings = (state) => fromLoanDetail.getSettings(state.loanDetail)

export const getLoanSetupRate = (state, rateType) => {
  return fromLoanDetail.getLoanSetupRate(state.loanDetail, rateType)
}

export const getLoanDetailAmountError = (state, rateType) => {
  switch(rateType) {
    case 'fixed':
      return fromLoanDetail.getFixedAmountError(state.loanDetail)
    default:
      return undefined
  }
}

export const getLoanDetailAmount = (state, rateType) => {
  const loanAmount = state.loanApplication.loanAmount
  const fixedAmount = fromLoanDetail.getFixedAmount(state.loanDetail)
  if (rateType == 'fixed') {
    return fixedAmount || loanAmount
  } else {
    return loanAmount - (fixedAmount || loanAmount)
  }
}

// EMPLOYMENT REDUCER
export const getEmployment = (state, id) => fromEmployment.getEmployment(state.employment, id)
export const getEmployments = (state) => fromEmployment.getEmployments(state.employment)
export const getEmploymentIds = (state) => fromEmployment.getEmploymentIds(state.employment)


// Options Reducer
export const getProviderSelectOptions = (state, service) => fromOptions.getProviderSelectOptions(state.options, service)
export const getOccupationSelectOptions = (state) => fromOptions.getOccupationSelectOptions(state.options)
export const getEmploymentTypeSelectOptions = (state) => fromOptions.getEmploymentTypeSelectOptions(state.options)
export const getRepaymentTypeSelectOptions = (state, service) => fromOptions.getRepaymentTypeSelectOptions(state.options, service)
export const getRepaymentWaysTypeSelectOptions = (state, service) => fromOptions.getRepaymentWaysTypeSelectOptions(state.options, service)
export const getRepaymentWaysTypeOptions = (state, service) => fromOptions.getRepaymentWaysTypeOptions(state.options, service)
export const getIncomeOptions = (state) => fromOptions.getIncomeOptions(state.options)

// OPTIONS REDUCERS
export default reducer;
